import { useEffect, useState, type FormEvent } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  DataTable,
  EmptyState,
  FilterBar,
  FormField,
  Input,
  LoadingState,
  Select,
} from '@sakhtar/design-system/react';
import { listOperatorAudit, type OperatorAuditEvent } from '../../lib/operatorApi.js';
import { JalaliDateInput } from '../../lib/date/JalaliDateInput.js';
import { OperatorPage } from './OperatorShell.js';

export const ACTION_LABELS: Record<string, string> = {
  'session.login': 'ورود اپراتور به سامانه',
  'session.logout': 'خروج اپراتور',
  'platform.organization.create': 'ایجاد سازمان جدید',
  'platform.organization.suspend': 'تعلیق سازمان',
  'platform.organization.reactivate': 'فعال‌سازی مجدد سازمان',
  'platform.trial.extend': 'تمدید دوره آزمایشی',
  'platform.trial.shorten': 'کوتاه‌کردن دوره آزمایشی',
  'platform.trial.cancel': 'لغو دوره آزمایشی',
  'platform.plan.create': 'ایجاد طرح اشتراک',
  'platform.plan.update': 'ویرایش طرح اشتراک',
  'platform.plan.activate': 'فعال‌سازی طرح',
  'platform.plan.deactivate': 'غیرفعال‌سازی طرح',
  'platform.organization.support-login': 'ورود پشتیبانی به پنل سازمان',
  'platform.organization.users-list': 'مشاهدهٔ فهرست کاربران سازمان',
  'platform.user.birthdays-list': 'مشاهدهٔ تولدهای نزدیک کاربران همهٔ سازمان‌ها',
  'platform.organization.change-plan': 'تغییر طرح و سقف صندلی سازمان',
};

export function AuditPage() {
  const [organizationRef, setOrganizationRef] = useState('');
  const [action, setAction] = useState('');
  const [actor, setActor] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [rows, setRows] = useState<readonly OperatorAuditEvent[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function load(event?: FormEvent) {
    event?.preventDefault();
    setError(null);
    try {
      const events = await listOperatorAudit({
        ...(organizationRef.trim() === '' ? {} : { organizationRef: organizationRef.trim() }),
        ...(action.trim() === '' ? {} : { action: action.trim() }),
        ...(actor.trim() === '' ? {} : { actor: actor.trim() }),
        ...(from.trim() === '' ? {} : { from: from.trim() }),
        ...(to.trim() === '' ? {} : { to: to.trim() }),
      });
      setRows(events);
    } catch {
      setError('بارگذاری رویدادها ناموفق بود.');
    }
  }

  useEffect(() => {
    void load();
  }, []);

  return (
    <OperatorPage
      title="گزارش رویدادهای ممیزی کنسول"
      actions={
        <Button variant="secondary" type="button" onClick={() => void load()}>
          بروزرسانی داده‌ها
        </Button>
      }
    >
      <Card>
        <form onSubmit={(event) => void load(event)}>
          <FilterBar>
            <FormField label="سازمان">
              <Input
                dir="ltr"
                placeholder="نامک یا شناسه سازمان..."
                value={organizationRef}
                onChange={(event) => setOrganizationRef(event.target.value)}
              />
            </FormField>
            <FormField label="نوع عملیات">
              <Select value={action} onChange={(event) => setAction(event.target.value)}>
                <option value="">همه عملیات‌ها</option>
                <option value="session.login">ورود اپراتور</option>
                <option value="session.logout">خروج اپراتور</option>
                <option value="platform.organization.create">ایجاد سازمان</option>
                <option value="platform.organization.suspend">تعلیق سازمان</option>
                <option value="platform.organization.reactivate">فعال‌سازی مجدد</option>
                <option value="platform.trial.extend">تمدید آزمایشی</option>
                <option value="platform.trial.shorten">کوتاه‌کردن آزمایشی</option>
                <option value="platform.trial.cancel">لغو آزمایشی</option>
                <option value="platform.plan.create">ایجاد طرح</option>
                <option value="platform.plan.update">ویرایش طرح</option>
              </Select>
            </FormField>
            <FormField label="عامل / کاربر">
              <Input
                dir="ltr"
                placeholder="شناسه عامل..."
                value={actor}
                onChange={(event) => setActor(event.target.value)}
              />
            </FormField>
            <FormField label="از تاریخ">
              <JalaliDateInput value={from} onChange={setFrom} />
            </FormField>
            <FormField label="تا تاریخ">
              <JalaliDateInput value={to} onChange={setTo} />
            </FormField>
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
              <Button type="submit">اعمال فیلتر</Button>
              {organizationRef || action || actor || from || to ? (
                <Button
                  variant="secondary"
                  type="button"
                  onClick={() => {
                    setOrganizationRef('');
                    setAction('');
                    setActor('');
                    setFrom('');
                    setTo('');
                  }}
                >
                  پاک کردن
                </Button>
              ) : null}
            </div>
          </FilterBar>
        </form>
      </Card>

      {error ? (
        <Alert tone="danger" title="خطا">
          {error}
        </Alert>
      ) : null}

      {rows === null ? (
        <LoadingState />
      ) : rows.length === 0 ? (
        <EmptyState title="هیچ رویدادی با این شرایط یافت نشد." />
      ) : (
        <DataTable
          columns={[
            {
              key: 'when',
              header: 'زمان وقوع',
              render: (row) => (
                <span dir="ltr" style={{ fontSize: 13 }}>
                  {new Date(row.occurredAt).toLocaleString('fa-IR')}
                </span>
              ),
            },
            {
              key: 'action',
              header: 'عملیات',
              render: (row) => (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <strong style={{ fontSize: 13 }}>
                    {ACTION_LABELS[row.action] ?? row.action}
                  </strong>
                  <code dir="ltr" style={{ fontSize: 11, color: 'var(--sk-fg-muted)' }}>
                    {row.action}
                  </code>
                </div>
              ),
            },
            {
              key: 'actor',
              header: 'عامل اجرا',
              render: (row) => (
                <code dir="ltr" style={{ fontSize: 12 }}>
                  {row.actor}
                </code>
              ),
            },
            {
              key: 'org',
              header: 'سازمان مربوطه',
              render: (row) =>
                row.organizationRef ? (
                  <code dir="ltr" style={{ fontSize: 12 }}>
                    {row.organizationRef}
                  </code>
                ) : (
                  '—'
                ),
            },
            {
              key: 'status',
              header: 'وضعیت',
              render: (row) => {
                const s = row.status.toLowerCase();
                const tone =
                  s === 'completed' || s === 'success'
                    ? 'success'
                    : s === 'failed' || s === 'error'
                      ? 'danger'
                      : 'info';
                const label =
                  s === 'completed' || s === 'success'
                    ? 'موفق'
                    : s === 'failed' || s === 'error'
                      ? 'ناموفق'
                      : row.status;
                return <Badge tone={tone}>{label}</Badge>;
              },
            },
            {
              key: 'reason',
              header: 'دلیل / توضیحات',
              render: (row) => row.reason ?? '—',
            },
          ]}
          rows={[...rows]}
          rowKey={(row) =>
            `${row.occurredAt}:${row.action}:${row.actor}:${row.organizationRef ?? ''}`
          }
        />
      )}
      <p style={{ color: 'var(--sk-fg-muted)', fontSize: 13, marginTop: 16 }}>
        تمامی رخدادهای حساس و تغییرات سیستمی اپراتورها در این بخش به‌صورت غیرقابل دستکاری ثبت
        می‌شوند.
      </p>
    </OperatorPage>
  );
}
